import { useCallback, useEffect, useMemo, useState } from 'react';

const toProductType = (entityType) => (entityType === 'receta' ? 'recipe' : 'article');
const toEntityType = (productType) => (productType === 'recipe' ? 'receta' : 'articulo');

const mapRestaurants = (payload = []) =>
  payload.filter(Boolean).map((item) => ({
    id: item.id_boh ?? item.id,
    nombre: item.nombre || item.name || 'Sin nombre'
  }));

const mapArticles = (payload = []) =>
  payload.filter(Boolean).map((item) => ({
    id: item.id,
    nombre: item.nombre || item.name || 'Sin nombre',
    proceso: item.proceso_nombre || item.proceso || ''
  }));

const mapRecipes = mapArticles;

const normalizeException = (item = {}) => {
  const entityType = item.entidad_tipo || item.entity_type || null;
  const productType = item.tipo_producto || (entityType ? toProductType(entityType) : null) || 'article';
  const is24Hours = typeof item.es_24_horas === 'boolean' ? item.es_24_horas : null;
  return {
    ...item,
    sucursal_id: item.sucursal_id ?? item.restaurant_id ?? null,
    sucursal_nombre: item.sucursal_nombre ?? item.restaurant_name ?? '',
    entidad_tipo: entityType,
    entidad_id: item.entidad_id ?? null,
    producto_id: item.producto_id ?? item.entidad_id ?? null,
    producto_nombre: item.producto_nombre ?? item.entidad_nombre ?? '',
    tipo_producto: productType,
    tipo_excepcion: item.tipo_excepcion || (is24Hours === null ? '24h' : (is24Hours ? '24h' : 'custom')),
    activo: Boolean(item.activo ?? true)
  };
};

const normalizePayload = (values = {}) => {
  const entityType = toEntityType(values.tipo_producto || 'article');
  const rawSucursalIds = Array.isArray(values.sucursal_ids) ? values.sucursal_ids : [];
  const normalizedSucursalIds = rawSucursalIds
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  return {
    sucursal_ids: normalizedSucursalIds,
    sucursal_id: Number(values.sucursal_id) || normalizedSucursalIds[0] || null,
    entidad_tipo: entityType,
    entidad_id: Number(values.producto_id) || null,
    es_24_horas: values.tipo_excepcion === '24h',
    motivo: (values.motivo || '').trim(),
    fecha_inicio: values.fecha_inicio || null,
    fecha_fin: values.fecha_fin || null
  };
};

export const useAdminOperatingExceptions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const [options, setOptions] = useState({
    loading: false,
    error: null,
    restaurants: [],
    articles: [],
    recipes: []
  });

  const fetchExceptions = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/admin/api/operating-exceptions', { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las excepciones');
      }
      if (!signal?.aborted) {
        const normalized = (data.exceptions || []).map((item) => normalizeException(item));
        setItems(normalized);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar las excepciones');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchExceptions(controller.signal);
    return () => controller.abort();
  }, [fetchExceptions, refreshToken]);

  const loadOptions = useCallback(async (signal) => {
    setOptions((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [restaurantsRes, articlesRes, recipesRes] = await Promise.all([
        fetch('/admin/api/sucursales-dropdown', { signal }),
        fetch('/admin/api/articles-exceptions', { signal }),
        fetch('/admin/api/recipes-exceptions', { signal })
      ]);
      const [restaurantsData, articlesData, recipesData] = await Promise.all([
        restaurantsRes.json(),
        articlesRes.json(),
        recipesRes.json()
      ]);
      if (signal?.aborted) {
        return;
      }
      if (!restaurantsRes.ok || restaurantsData?.error) {
        throw new Error(restaurantsData?.error || 'No se pudieron obtener las sucursales');
      }
      if (!articlesRes.ok || articlesData?.error) {
        throw new Error(articlesData?.error || 'No se pudieron obtener los artículos');
      }
      if (!recipesRes.ok || recipesData?.error) {
        throw new Error(recipesData?.error || 'No se pudieron obtener las recetas');
      }
      setOptions({
        loading: false,
        error: null,
        restaurants: mapRestaurants(restaurantsData.sucursales || restaurantsData.restaurants || restaurantsData.items || []),
        articles: mapArticles(articlesData.articles || []),
        recipes: mapRecipes(recipesData.recipes || [])
      });
    } catch (err) {
      if (!signal?.aborted) {
        setOptions((prev) => ({
          ...prev,
          loading: false,
          error: err?.message || 'No se pudieron cargar las opciones'
        }));
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadOptions(controller.signal);
    return () => controller.abort();
  }, [loadOptions]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const saveException = useCallback(async (values) => {
    const payload = normalizePayload(values);
    const hasSucursal = Boolean(payload.sucursal_id) || (payload.sucursal_ids || []).length > 0;
    if (!hasSucursal || !payload.entidad_id || !payload.entidad_tipo) {
      throw new Error('Selecciona la sucursal y el producto.');
    }
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/operating-exceptions/${values.id}` : '/admin/api/operating-exceptions';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar la excepción');
    }
    return data;
  }, []);

  const deleteException = useCallback(async (id) => {
    const response = await fetch(`/admin/api/operating-exceptions/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar la excepción');
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    loading,
    error,
    options,
    refresh,
    saveException,
    deleteException
  }), [items, loading, error, options, refresh, saveException, deleteException]);
};
